/*
  Te Araroa trail-pass registration.

  Owns the one form on sections/te-araroa-register.liquid: shrinks the
  pass photo in the browser, validates, posts JSON to the Google Apps
  Script endpoint the section carries in data-endpoint, and swaps the
  panel into the thank-you state. See docs/te-araroa/README.md for the
  receiving end.

  Why the photo is resized here: a phone photo of a trail pass is 3 to
  12MB; the script only needs to show it in an email and keep it in
  Drive. 1600px on the long edge at JPEG 0.82 lands at a few hundred
  KB, uploads in a second on trail wifi, and stays readable. PDFs and
  anything the browser cannot decode (HEIC on some Androids) go through
  untouched, under the size cap.

  Why text/plain: Apps Script web apps do not answer the CORS preflight
  an application/json POST triggers. A text/plain body is a "simple"
  request, so the browser sends it straight through and the script's
  redirect-then-JSON answer carries Access-Control-Allow-Origin: *.
*/
if (!customElements.get('te-araroa-register')) {
	customElements.define(
		'te-araroa-register',
		class TeAraroaRegister extends HTMLElement {
			constructor() {
				super()
				this.form = this.querySelector('[data-form]')
				if (!this.form) return // approved state: nothing to wire

				this.endpoint = (this.dataset.endpoint || '').trim()
				this.fallbackEmail = this.dataset.fallbackEmail || ''
				this.maxBytes = Number(this.dataset.maxBytes) || 10 * 1048576
				this.panel = this.querySelector('[data-panel]')
				this.fileInput = this.querySelector('[data-file]')
				this.drop = this.querySelector('[data-drop]')
				this.dropEmpty = this.querySelector('[data-drop-empty]')
				this.dropPicked = this.querySelector('[data-drop-picked]')
				this.thumb = this.querySelector('[data-thumb]')
				this.pickedName = this.querySelector('[data-picked-name]')
				this.submit = this.querySelector('[data-submit]')
				this.submitLabel = this.querySelector('[data-submit-label]')
				this.idleLabel = this.submitLabel ? this.submitLabel.textContent : ''
				this.errorTemplate = this.querySelector('[data-error-template]')
				this.thanksTemplate = this.querySelector('[data-thanks-template]')
				this.returningTemplate = this.querySelector('[data-returning-template]')

				// A successful send is remembered on this device (per season) so
				// a walker coming back sees "you sent us your pass on…" instead
				// of an empty form and does not register twice. Private to the
				// browser, never sent anywhere; cleared by "Register a different
				// pass". Storage can throw (private mode, blocked site data), so
				// every touch is guarded and the form is the fallback.
				this.storageKey = 'co_te_araroa_registered_' + (this.dataset.season || '').replace(/[^0-9a-z]/gi, '')

				this.prepared = null // { name, type, data(base64), bytes }
				this.preparing = null // in-flight Promise while a file is being read

				const remembered = this.readRemembered()
				if (remembered) this.showReturning(remembered)

				this.fileInput.addEventListener('change', () => this.onFile())
				this.form.addEventListener('submit', (evt) => this.onSubmit(evt))

				// Clear a field's error chip as soon as the user edits it,
				// matching the contact form.
				this.form.querySelectorAll('input').forEach((input) => {
					input.addEventListener('input', () => this.clearError(input))
				})

				// Drag-and-drop onto the tan surface (desktop nicety; the
				// label click is the real path on phones).
				;['dragenter', 'dragover'].forEach((type) =>
					this.drop.addEventListener(type, (evt) => {
						evt.preventDefault()
						this.drop.classList.add('is-dragover')
					})
				)
				;['dragleave', 'drop'].forEach((type) =>
					this.drop.addEventListener(type, (evt) => {
						evt.preventDefault()
						this.drop.classList.remove('is-dragover')
					})
				)
				this.drop.addEventListener('drop', (evt) => {
					const file = evt.dataTransfer && evt.dataTransfer.files && evt.dataTransfer.files[0]
					if (!file) return
					this.setFile(file)
				})
			}

			/* ---------- file handling ---------- */

			onFile() {
				const file = this.fileInput.files && this.fileInput.files[0]
				if (!file) {
					this.showEmpty()
					return
				}
				this.setFile(file)
			}

			setFile(file) {
				this.clearError(this.fileInput)
				this.prepared = null

				const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
				const isImage = /^image\//.test(file.type) || /\.(heic|heif)$/i.test(file.name)
				if (!isPdf && !isImage) {
					this.showEmpty()
					this.showError(this.fileInput, 'That file type will not open at our end. A photo, screenshot or PDF works.')
					return
				}

				this.showPicked(file.name, null)
				this.drop.classList.add('is-busy')

				const job = (isImage ? this.shrinkImage(file) : Promise.resolve(null))
					.then((shrunk) => {
						const blob = shrunk || file
						if (blob.size > this.maxBytes) {
							throw new Error('too-big')
						}
						return this.toBase64(blob).then((data) => ({
							name: shrunk ? file.name.replace(/\.[^.]+$/, '') + '.jpg' : file.name,
							type: shrunk ? 'image/jpeg' : file.type || (isPdf ? 'application/pdf' : 'application/octet-stream'),
							data,
							bytes: blob.size,
							previewUrl:
								shrunk || /^image\/(jpeg|png|webp|gif)$/.test(file.type)
									? URL.createObjectURL(blob)
									: null,
						}))
					})
					.then((prepared) => {
						if (this.preparing !== job) return // superseded by a newer pick
						this.prepared = prepared
						this.showPicked(file.name, prepared.previewUrl)
					})
					.catch((err) => {
						if (this.preparing !== job) return
						this.showEmpty()
						this.fileInput.value = ''
						const mb = Math.round(this.maxBytes / 1048576)
						this.showError(
							this.fileInput,
							err && err.message === 'too-big'
								? 'That file is over ' + mb + 'MB. A screenshot of the pass is plenty.'
								: 'We could not read that file. Try a screenshot of the pass instead.'
						)
					})
					.finally(() => {
						if (this.preparing === job) {
							this.preparing = null
							this.drop.classList.remove('is-busy')
						}
					})
				this.preparing = job
			}

			// Resolves to a JPEG Blob, or null when the browser cannot decode
			// the file (then the original is sent as-is, size permitting).
			shrinkImage(file) {
				const MAX_EDGE = 1600
				const decode = () =>
					typeof createImageBitmap === 'function'
						? createImageBitmap(file)
						: new Promise((resolve, reject) => {
								const url = URL.createObjectURL(file)
								const img = new Image()
								img.onload = () => {
									URL.revokeObjectURL(url)
									resolve(img)
								}
								img.onerror = () => {
									URL.revokeObjectURL(url)
									reject(new Error('decode'))
								}
								img.src = url
						  })

				return decode()
					.then((source) => {
						const w = source.width || source.naturalWidth
						const h = source.height || source.naturalHeight
						if (!w || !h) throw new Error('decode')
						const scale = Math.min(1, MAX_EDGE / Math.max(w, h))
						const canvas = document.createElement('canvas')
						canvas.width = Math.round(w * scale)
						canvas.height = Math.round(h * scale)
						const ctx = canvas.getContext('2d')
						ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
						if (source.close) source.close()
						return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82))
					})
					.then((blob) => blob || null)
					.catch(() => null)
			}

			toBase64(blob) {
				return new Promise((resolve, reject) => {
					const reader = new FileReader()
					reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
					reader.onerror = () => reject(reader.error)
					reader.readAsDataURL(blob)
				})
			}

			showEmpty() {
				this.prepared = null
				this.dropEmpty.hidden = false
				this.dropPicked.hidden = true
				this.thumb.hidden = true
				if (this.thumb.src) {
					URL.revokeObjectURL(this.thumb.src)
					this.thumb.removeAttribute('src')
				}
				this.drop.classList.remove('has-file')
			}

			showPicked(name, previewUrl) {
				this.dropEmpty.hidden = true
				this.dropPicked.hidden = false
				this.pickedName.textContent = name
				if (previewUrl) {
					this.thumb.src = previewUrl
					this.thumb.hidden = false
				} else {
					this.thumb.hidden = true
				}
				this.drop.classList.add('has-file')
			}

			/* ---------- validation + errors ---------- */

			groupFor(input) {
				return input ? input.closest('.ta-register__field-group') : null
			}

			clearError(input) {
				const group = this.groupFor(input)
				if (!group) return
				const existing = group.querySelector('.ta-register__error')
				if (existing) existing.remove()
				input.removeAttribute('aria-invalid')
			}

			clearFormError() {
				const existing = this.form.querySelector('.ta-register__actions .ta-register__error')
				if (existing) existing.remove()
			}

			showError(input, message) {
				const group = this.groupFor(input)
				if (!group || !this.errorTemplate) return
				this.clearError(input)
				const chip = this.errorTemplate.content.firstElementChild.cloneNode(true)
				chip.querySelector('.ta-register__error-text').textContent = message
				group.appendChild(chip)
				input.setAttribute('aria-invalid', 'true')
			}

			showFormError(message) {
				this.clearFormError()
				if (!this.errorTemplate) return
				const chip = this.errorTemplate.content.firstElementChild.cloneNode(true)
				chip.querySelector('.ta-register__error-text').textContent = message
				this.form.querySelector('.ta-register__actions').appendChild(chip)
			}

			validate() {
				let first = null
				// Each field is re-judged from scratch: a chip from an earlier
				// attempt clears when the field is now fine (autofill and
				// password managers set values without firing input events).
				const name = this.form.elements.namedItem('name')
				const email = this.form.elements.namedItem('email')
				if (!name.value.trim()) {
					this.showError(name, 'Your name, so we can match it to the pass.')
					first = first || name
				} else {
					this.clearError(name)
				}
				const emailValue = email.value.trim()
				if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
					this.showError(email, 'An email address we can reach you on.')
					first = first || email
				} else {
					this.clearError(email)
				}
				if (!this.prepared) {
					this.showError(
						this.fileInput,
						this.preparing ? 'One moment, your pass is still loading.' : 'Add a photo or screenshot of your trail pass.'
					)
					first = first || this.fileInput
				} else {
					this.clearError(this.fileInput)
				}
				// The file input is visually hidden under its label; focusing
				// it shows the ring on the tan surface via :has(:focus-visible).
				if (first) first.focus()
				return !first
			}

			/* ---------- submit ---------- */

			setBusy(busy) {
				this.submit.disabled = busy
				this.form.classList.toggle('is-sending', busy)
				if (this.submitLabel) this.submitLabel.textContent = busy ? 'Sending' : this.idleLabel
			}

			onSubmit(evt) {
				evt.preventDefault()
				this.clearFormError()

				// Honeypot: a filled hidden field is a bot. Say thanks, send nothing.
				const hp = this.form.querySelector('[data-hp]')
				if (hp && hp.value) {
					this.showThanks(this.form.elements.namedItem('email').value.trim())
					return
				}

				if (!this.validate()) return

				if (!this.endpoint) {
					this.showFormError(
						"We're not taking uploads here just yet. Email a photo of your pass to " + this.fallbackEmail + ' and we will set you up.'
					)
					return
				}

				const payload = {
					source: 'te-araroa-register',
					season: this.dataset.season || '',
					page: window.location.href,
					name: this.form.elements.namedItem('name').value.trim(),
					email: this.form.elements.namedItem('email').value.trim(),
					file: {
						name: this.prepared.name,
						type: this.prepared.type,
						bytes: this.prepared.bytes,
						data: this.prepared.data,
					},
				}

				this.setBusy(true)
				const controller = typeof AbortController === 'function' ? new AbortController() : null
				const timer = controller ? setTimeout(() => controller.abort(), 45000) : null

				fetch(this.endpoint, {
					method: 'POST',
					headers: { 'Content-Type': 'text/plain;charset=utf-8' },
					body: JSON.stringify(payload),
					signal: controller ? controller.signal : undefined,
				})
					.then((res) => res.json())
					.then((json) => {
						if (!json || !json.ok) throw new Error((json && json.error) || 'bad-response')
						this.showThanks(payload.email)
					})
					.catch((err) => {
						this.setBusy(false)
						const why = err && err.message
						this.showFormError(
							why === 'size'
								? 'That file was too large for us to take. A screenshot of the pass is plenty.'
								: 'That did not send. Try again in a moment, or email a photo of your pass to ' + this.fallbackEmail + '.'
						)
					})
					.finally(() => {
						if (timer) clearTimeout(timer)
					})
			}

			showThanks(email) {
				if (!this.thanksTemplate) return
				this.writeRemembered({ email: email || '', at: Date.now() })
				const state = this.thanksTemplate.content.firstElementChild.cloneNode(true)
				state.querySelectorAll('.ta-register__state-body').forEach((el) => {
					el.textContent = el.textContent.replace('{email}', email || 'your email')
				})
				this.form.replaceWith(state)
				this.form = null
				state.focus({ preventScroll: true })
			}

			/* ---------- remembered registration ---------- */

			readRemembered() {
				try {
					const raw = window.localStorage.getItem(this.storageKey)
					if (!raw) return null
					const record = JSON.parse(raw)
					if (!record || !record.at) return null
					return record
				} catch (err) {
					return null
				}
			}

			writeRemembered(record) {
				try {
					window.localStorage.setItem(this.storageKey, JSON.stringify(record))
				} catch (err) {
					/* storage unavailable: the thank-you still shows this visit */
				}
			}

			forgetRemembered() {
				try {
					window.localStorage.removeItem(this.storageKey)
				} catch (err) {
					/* nothing to clear */
				}
			}

			// Hides the form (kept in the DOM so "Register a different pass"
			// can bring it straight back) and shows the returning state.
			showReturning(record) {
				if (!this.returningTemplate || !this.form) return
				const state = this.returningTemplate.content.firstElementChild.cloneNode(true)
				const when = new Date(record.at)
				const dateText = isNaN(when.getTime())
					? 'recently'
					: when.toLocaleDateString('en-NZ', { day: 'numeric', month: 'long' })
				state.querySelectorAll('.ta-register__state-heading, .ta-register__state-body').forEach((el) => {
					el.textContent = el.textContent
						.replace('{date}', dateText)
						.replace('{email}', record.email || 'you')
				})
				const again = state.querySelector('[data-register-again]')
				if (again) {
					again.addEventListener('click', () => {
						this.forgetRemembered()
						state.remove()
						this.form.hidden = false
						const first = this.form.querySelector('input:not([type="hidden"]):not([data-hp])')
						if (first) first.focus({ preventScroll: true })
					})
				}
				this.form.hidden = true
				this.form.insertAdjacentElement('beforebegin', state)
			}
		}
	)
}
